import * as React from 'react';
import { Config } from '../../../helper';
import sortIcon from '../../../../faceted-search/react/icons/sort.svg';
import Cell from './Cell';
import SanitizedHtml from '../SanitizedHtml';
import { groupRows, sortRows } from './helper';

// Toggle `with-scroll` when the table overflows horizontally, so the scroll-shadow and
// styled scrollbar appear only when there's something to scroll (mirrors the grouped
// table). Applied imperatively — React leaves the class alone since `className` is stable.
function applyScrollClass(el) {
  if (!el) return;
  el.classList.toggle('with-scroll', el.scrollWidth > el.clientWidth);
}

// One configured group → one table. Rendered from config (fixed order); body comes from the
// shared result set filtered by groupBy. An empty group shows the standard no-results message.
// Sortable columns get clickable headers that sort the rows client-side, within this group only.
export default function GroupTable({group, results, groupBy, tableClassName}) {
  const {caption, footer, htmlId, columns = []} = group;
  const [sort, setSort] = React.useState({field: null, dir: 'asc', numeric: false});
  const rows = sortRows(groupRows(results, groupBy, group.match), sort);

  // Toggle direction when re-clicking the active column, otherwise sort the new column ascending.
  const toggleSort = column => {
    const numeric = column.type === Config.ColumnTypes.Number;
    setSort(prev => prev.field === column.field
      ? {field: column.field, dir: prev.dir === 'asc' ? 'desc' : 'asc', numeric}
      : {field: column.field, dir: 'asc', numeric});
  };

  const tableClass = ['nv-table', tableClassName].filter(Boolean).join(' ');

  // Detect horizontal overflow → `with-scroll` (drives the scroll-shadow + styled
  // scrollbar). Mobile scrollbars are hidden, so the shadow is the only "scroll for more"
  // cue. Re-measured on data change, on scroll, and on viewport resize.
  const tableElRef = React.useRef(null);
  React.useEffect(() => {
    const el = tableElRef.current;
    if (!el) return undefined;
    const handle = () => applyScrollClass(el);
    el.addEventListener('scroll', handle);
    window.addEventListener('resize', handle);
    handle();
    return () => {
      el.removeEventListener('scroll', handle);
      window.removeEventListener('resize', handle);
    };
  }, []);

  return (
    <section className='table-group group' id={htmlId || undefined}>
      {caption && <h3 className='table-group__caption h--smaller'>{caption}</h3>}

      {rows.length === 0 ? (
        <p className='table-group__empty'>{Config.I18n.Hits.NoResults}</p>
      ) : (
        <div className='table-wrapper'>
          <div className={tableClass} ref={tableElRef}>
          <table>
            <thead>
              <tr>
                {columns.map((column, idx) => {
                  const label = column.label || column.field;
                  // List columns hold multi-value arrays with no meaningful sort order — never sortable.
                  if (!column.sortable || column.type === Config.ColumnTypes.List) return <th key={idx}>{label}</th>;

                  const active = sort.field === column.field;
                  const ariaSort = active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none';
                  const sortLabel = active
                    ? `Sort by ${label}, ${sort.dir === 'asc' ? 'ascending' : 'descending'}`
                    : `Sort by ${label}`;
                  return (
                    <th key={idx} aria-sort={ariaSort} className='nv-table__th--sortable'>
                      <button type='button' className='nv-table__sort' aria-label={sortLabel} onClick={() => toggleSort(column)}>
                        <span aria-hidden='true'>{label}</span>
                        <img className='nv-table__sort-indicator' src={sortIcon} alt=''/>
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((result, rowIdx) => {
                const rowId = result?._meta?.id || rowIdx;
                return (
                  <tr key={rowId}>
                    {columns.map((column, colIdx) => (
                      <Cell key={colIdx} result={result} column={column}/>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Admin-authored footer supports HTML; SanitizedHtml applies the shared sanitize
          policy and renders a div so block-level markup nests validly. */}
      {footer && <SanitizedHtml className='table-group__footer' html={footer}/>}
    </section>
  );
}
